import React, { useEffect, useState } from 'react';
import { MessageCircle, X, Loader2, Pencil, Trash2, Save, Send } from 'lucide-react';
import {
  listOwnerComments,
  createOwnerComment,
  updateOwnerComment,
  deleteOwnerComment,
} from '../lib/api';
import SanctuaryModerationDialog, { parseSanctuaryError } from './SanctuaryModerationDialog';
import { useConfirmDialog } from './ConfirmDialog';

/**
 * Owner-comments manager modal. Used from PrayerCard, ManagePrayers, and
 * WatchPrayer. Anyone can OPEN it to read; only the prayer owner sees the
 * write controls (add / edit / delete). The parent decides `canManage`.
 */
const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso || '';
  }
};

const OwnerCommentsDialog = ({ open, onClose, prayerId, canManage, onCountChange }) => {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [err, setErr] = useState(null);
  // Sanctuary Moderation surfaced via the shared red modal.
  const [sanctuary, setSanctuary] = useState(null);
  const { confirm, dialog: confirmDialogEl } = useConfirmDialog();

  useEffect(() => {
    if (!open || !prayerId) return;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    listOwnerComments(prayerId)
      .then((data) => { if (!cancelled) setComments(Array.isArray(data) ? data : []); })
      .catch(() => { if (!cancelled) setErr('Could not load comments.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, prayerId]);

  if (!open) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text) return;
    setPosting(true);
    setErr(null);
    try {
      const created = await createOwnerComment(prayerId, text);
      setComments((prev) => [created, ...prev]);
      setDraft('');
      onCountChange?.((comments.length || 0) + 1);
    } catch (e2) {
      const sanct = parseSanctuaryError(e2);
      if (sanct) setSanctuary(sanct);
      else setErr(e2?.response?.data?.detail || 'Could not post the comment.');
    } finally {
      setPosting(false);
    }
  };

  const handleSaveEdit = async (commentId) => {
    const text = editText.trim();
    if (!text) return;
    try {
      const saved = await updateOwnerComment(commentId, text);
      setComments((prev) => prev.map((c) => (c.comment_id === commentId ? saved : c)));
      setEditingId(null);
      setEditText('');
    } catch (e2) {
      const sanct = parseSanctuaryError(e2);
      if (sanct) setSanctuary(sanct);
      else setErr(e2?.response?.data?.detail || 'Could not save the edit.');
    }
  };

  const handleDelete = async (commentId) => {
    const ok = await confirm({
      title: 'Delete this comment?',
      description: 'The comment will be permanently removed. This cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteOwnerComment(commentId);
      setComments((prev) => prev.filter((c) => c.comment_id !== commentId));
      onCountChange?.(Math.max(0, comments.length - 1));
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Could not delete the comment.');
    }
  };

  return (
    <>
    <SanctuaryModerationDialog
      open={Boolean(sanctuary)}
      variant={sanctuary?.variant}
      reason={sanctuary?.reason}
      source={sanctuary?.source}
      onClose={() => setSanctuary(null)}
    />
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => { e.stopPropagation(); onClose(); }}
      data-testid="owner-comments-dialog"
    >
      <div
        className="w-full max-w-lg max-h-[85vh] flex flex-col bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center gap-2">
            <MessageCircle size={18} className="text-amber-500" />
            <h3 className="text-base font-semibold" data-testid="owner-comments-title">Owner comments</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="close-owner-comments-btn"
            className="p-1.5 rounded-full hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
            aria-label="Close comments"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="owner-comments-list">
          {loading && (
            <div className="flex items-center justify-center gap-2 text-sm text-zinc-500 py-8">
              <Loader2 className="animate-spin" size={14} /> Loading…
            </div>
          )}
          {!loading && comments.length === 0 && (
            <div className="text-center text-sm text-zinc-500 py-8" data-testid="owner-comments-empty">
              No comments yet.
            </div>
          )}
          {!loading && comments.map((c) => {
            const isEditing = editingId === c.comment_id;
            return (
              <div
                key={c.comment_id}
                className="rounded-lg border border-zinc-200 dark:border-zinc-800 p-3"
                data-testid={`owner-comment-${c.comment_id}`}
              >
                {isEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      className="w-full min-h-[70px] text-sm p-2 rounded-md bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700"
                      data-testid={`owner-comment-edit-input-${c.comment_id}`}
                    />
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => { setEditingId(null); setEditText(''); }}
                        className="px-3 py-1.5 text-xs rounded-md hover:bg-zinc-100 dark:hover:bg-zinc-800"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleSaveEdit(c.comment_id)}
                        data-testid={`owner-comment-save-edit-${c.comment_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md bg-amber-500 text-white hover:bg-amber-600"
                      >
                        <Save size={12} /> Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="text-sm whitespace-pre-wrap break-words">{c.text}</div>
                    <div className="mt-2 flex items-center justify-between text-[11px] text-zinc-500">
                      <span title={c.created_at}>{fmtDate(c.created_at)}</span>
                      {canManage && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() => { setEditingId(c.comment_id); setEditText(c.text); }}
                            data-testid={`owner-comment-edit-btn-${c.comment_id}`}
                            className="p-1 rounded hover:bg-zinc-100 dark:hover:bg-zinc-800"
                            aria-label="Edit comment"
                            title="Edit"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(c.comment_id)}
                            data-testid={`owner-comment-delete-btn-${c.comment_id}`}
                            className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-950/40 text-red-500"
                            aria-label="Delete comment"
                            title="Delete"
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>

        {canManage && (
          <form
            onSubmit={handleCreate}
            className="px-5 py-3 border-t border-zinc-200 dark:border-zinc-800 flex items-end gap-2"
            data-testid="owner-comment-new-form"
          >
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a comment on your prayer…"
              rows={2}
              className="flex-1 text-sm p-2 rounded-md bg-zinc-50 dark:bg-zinc-800 border border-zinc-200 dark:border-zinc-700 resize-none"
              data-testid="owner-comment-new-input"
            />
            <button
              type="submit"
              disabled={posting || !draft.trim()}
              data-testid="owner-comment-submit-btn"
              className="h-10 px-3 rounded-md bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1"
            >
              {posting ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
            </button>
          </form>
        )}

        {err && (
          <div className="px-5 py-2 text-xs text-red-600 dark:text-red-400 border-t border-red-200 dark:border-red-900/50" data-testid="owner-comments-error">
            {err}
          </div>
        )}
      </div>
      {confirmDialogEl}
    </div>
    </>
  );
};

export default OwnerCommentsDialog;
